import moment from 'moment';
import API from 'utils/api';
import { getTiposDeDescuentos } from 'api/recaudacion/tiposDeDescuento';
import { padronesEnum } from 'views/Tramites/GPM/enums';

export const baseURI = 'recaudacion/aplicacion-masiva-de-descuentos/';

// Tipos de aplicación que soporta la aplicación masiva en backend:
// Porcentaje Fijo, Importe Fijo, Fórmula y Tarifa
export const TIPOS_DE_APLICACION_SOPORTADOS = [1, 3, 5, 6];
// Estados globales con los que el backend acepta el tipo de descuento
const ESTADOS_GLOBALES_VIGENTES = [1, 2, 4];

// Varios tipos de descuento por ejecución (aplicación y cancelación)
export const PERMITE_VARIOS_DESCUENTOS = true;

// Largo máximo de Observaciones y motivo de cancelación: se copian en cada descuento
export const MAX_LONGITUD_TEXTO = 500;

// Intervalo sugerido por backend para consultar el estatus (5-10 s)
export const INTERVALO_ESTATUS_MS = 7000;

export const ESTATUS_DE_TAREA = Object.freeze({
  PROCESANDO: 'procesando',
  TERMINADO: 'terminado',
  ERROR: 'error',
});

const MSG_ERROR_INTERNO = 'Ocurrió un error al procesar los descuentos. Revise el correo de resultado o intente de nuevo; si persiste, contacte a soporte.';

// Errores no controlados del backend: pueden traer el traceback o el texto de la excepción
const esErrorInterno = (mensaje) => /^(Hubo un error al generar|Error al procesar)/i
  .test(mensaje || '');

/**
 * Mensaje para mostrar al usuario. Los errores de validación del backend se muestran tal cual;
 * los errores internos se sustituyen por un mensaje genérico.
 */
const getMensajeDeError = (err, mensajePorDefecto) => {
  const mensaje = err?.response?.data?.error
    || err?.response?.data?.detail
    || mensajePorDefecto;
  return esErrorInterno(mensaje) ? MSG_ERROR_INTERNO : mensaje;
};

// Registra solo estatus y mensaje: el error de axios incluye el body de la petición
// (con la contraseña) y los headers de autorización
const logError = (err) => {
  const mensaje = err?.response?.data?.error || err?.message;
  // eslint-disable-next-line no-console
  console.error('[descuentosMasivos]', err?.response?.status, esErrorInterno(mensaje) ? 'Error interno del servidor' : mensaje);
};

/**
 * Indica si el error del backend es por la contraseña (vacía o incorrecta), para que la
 * pantalla permita reintentar en lugar de cerrar el proceso
 * @param {string} error
 * @returns {boolean}
 */
export const esErrorDeContrasena = (error) => /contraseña es incorrecta|campo password/i
  .test(error || '');

/**
 * Indica si el backend desactivó la cuenta por exceder los intentos de contraseña
 * @param {string} error
 * @returns {boolean}
 */
export const esCuentaDesactivada = (error) => /cuenta fue desactivada/i.test(error || '');

/**
 * GET Tipos de descuento que se pueden aplicar de forma masiva al padrón predial:
 * especiales, con tipo de aplicación soportado, vigentes y que el usuario puede aplicar.
 * @returns {Promise<Array<object>>}
 */
export const getTiposDeDescuentoParaAplicacionMasiva = async () => {
  const tiposDeDescuento = await getTiposDeDescuentos({
    es_descuento_especial: true,
    padron: padronesEnum.PREDIO,
  });
  const hoy = moment().startOf('day');
  return tiposDeDescuento.filter((tipo) => tipo.es_descuento_especial
    && tipo.puede_aplicar
    && TIPOS_DE_APLICACION_SOPORTADOS.includes(tipo.tipo_de_aplicacion)
    && ESTADOS_GLOBALES_VIGENTES.includes(tipo.estados_globales)
    && !(tipo.fecha_fin && moment(tipo.fecha_fin).isBefore(hoy, 'day')));
};

/**
 * Arma el body del POST de aplicación. El backend aplica primero los descuentos sobre base
 * y después los de recargo/multa, actualización y gasto; si un tipo no es válido no aplica ninguno.
 * @param {object} values
 * @param {Array<string>} values.cuentas
 * @param {Array<{tipo_de_descuento: number, variables: Array<object>}>} values.descuentos
 * @param {string} [values.comentarios]
 * @returns {object}
 */
const buildPayloadAplicacion = ({ cuentas, descuentos, comentarios }) => {
  const payload = {
    cuentas,
    tipos_de_descuento: descuentos.map(({ tipo_de_descuento, variables }) => ({
      tipo_de_descuento,
      variables: variables || [],
    })),
  };
  if (comentarios) payload.comentarios = comentarios;
  return payload;
};

/**
 * POST encola la aplicación masiva de descuentos; el Excel llega por correo y por estatus
 * @param {object} values
 * @param {Array<string>} values.cuentas - Claves catastrales municipales
 * @param {Array<{tipo_de_descuento: number, variables: Array<object>}>} values.descuentos
 * @param {string} [values.comentarios] - Observaciones (quién autoriza)
 * @param {string} values.password - Contraseña del usuario (reautenticación)
 * @returns {Promise<{ok: boolean, data?: {task_id: string}, error?: string}>}
 */
export const aplicarDescuentosMasivos = async ({ password, ...values }) => {
  try {
    const response = await API.post(baseURI, { ...buildPayloadAplicacion(values), password });
    return { ok: true, data: response.data };
  } catch (err) {
    // No se usa onError: registraría el error completo de axios, que incluye el body
    // con la contraseña
    logError(err);
    return { ok: false, error: getMensajeDeError(err, 'No fue posible iniciar la aplicación de descuentos') };
  }
};

/**
 * GET estatus de la aplicación masiva. El resultado final se entrega una sola vez.
 * @param {string} taskId
 * @returns {Promise<{estatus: string, data?: object, error?: string}>}
 */
export const getEstatusAplicacionMasiva = async (taskId) => {
  try {
    const response = await API.get(`${baseURI}estatus/`, { params: { task_id: taskId } });
    if (response.data?.url_descarga) {
      return { estatus: ESTATUS_DE_TAREA.TERMINADO, data: response.data };
    }
    return { estatus: ESTATUS_DE_TAREA.PROCESANDO, data: response.data };
  } catch (err) {
    logError(err);
    return {
      estatus: ESTATUS_DE_TAREA.ERROR,
      error: getMensajeDeError(err, 'No fue posible consultar el estatus del proceso'),
    };
  }
};

/**
 * POST cancela los descuentos vigentes de uno o varios tipos en las cuentas;
 * se resuelve en la misma petición
 * @param {object} values
 * @param {Array<string>} values.cuentas - Claves catastrales municipales
 * @param {Array<number>} values.tipos_de_descuento
 * @param {string} values.motivo_de_cancelacion
 * @param {string} values.password - Contraseña del usuario (reautenticación)
 * @returns {Promise<{ok: boolean, data?: object, error?: string}>}
 */
export const cancelarDescuentosMasivos = async ({
  cuentas, tipos_de_descuento, motivo_de_cancelacion, password,
}) => {
  try {
    const response = await API.post(`${baseURI}cancelar/`, {
      cuentas,
      tipos_de_descuento,
      motivo_de_cancelacion,
      password,
    });
    return { ok: true, data: response.data };
  } catch (err) {
    logError(err);
    // Si no había descuentos que cancelar, el 400 también trae el resumen
    return {
      ok: false,
      data: err?.response?.data,
      error: getMensajeDeError(err, 'No fue posible cancelar los descuentos'),
    };
  }
};
